import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useCanonical, usePageMeta } from '@/hooks/useCanonical';
import { useBreadcrumbSchema } from '@/hooks/useBreadcrumbSchema';
import { ArrowRight, Star, CheckCircle2, Shield, Clock, Zap, Scissors, Droplets, CreditCard, Phone, Heart, Stethoscope, Sparkles, Users, Award, Snowflake, HandHeart, Target, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import BlogCard from '@/components/blog/BlogCard';
import CTASection from '@/components/CTASection';
const StickyCTABanner = lazy(() => import('@/components/StickyCTABanner'));
import ReviewsSection from '@/components/ReviewsSection';
import ContactSection from '@/components/ContactSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { blogArticles } from '@/data/blog-articles';
import { services, formules, PLANITY_URL } from '@/data/services';
const heroImg = '/images/hero-beauty.webp';

const stats = [
  { value: '4.5/5', label: 'Note Google' },
  { value: '8+', label: 'Soins proposés' },
  { value: '5', label: 'Expertes à votre service' },
  { value: '7j/7', label: 'Ouvert (sauf lundi)' },
];

const faqs: { question: string; answer: React.ReactNode }[] = [
  {
    question: 'Les soins sont-ils adaptés aux peaux noires et métissées ?',
    answer: (
      <div className="space-y-4">
        <p><strong>Oui, c'est notre spécialité.</strong> Carine Tchuente, infirmière diplômée, maîtrise les spécificités des peaux à phototypes foncés (III à VI).</p>
        <ul className="space-y-2.5">
          <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Épilation laser Diode</strong> — adapté à tous les phototypes, y compris peaux noires</span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Peelings doux adaptés</strong> — acide mandélique, kojique</span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>Protocoles anti-taches sécurisés</strong> — sans risque d'hyperpigmentation</span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /><span><strong>HIFU tous phototypes</strong> — ultrasons compatibles toutes carnations</span></li>
        </ul>
        <p className="text-sm italic text-muted-foreground/80">Nous accueillons toutes les carnations à Sevran et dans le 93.</p>
      </div>
    ),
  },
  {
    question: "Qu'est-ce que le HIFU et quels résultats puis-je espérer ?",
    answer: (
      <div className="space-y-4">
        <p>Le <strong>HIFU</strong> (High Intensity Focused Ultrasound) utilise des <strong>ultrasons focalisés</strong> pour stimuler la production de collagène en profondeur — <em>sans chirurgie ni injection</em>.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-4">
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
            <p className="font-heading font-bold text-primary text-lg">2-3 mois</p>
            <p className="text-xs text-muted-foreground mt-1">Résultats progressifs</p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
            <p className="font-heading font-bold text-primary text-lg">12-18 mois</p>
            <p className="text-xs text-muted-foreground mt-1">Durée des effets</p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
            <p className="font-heading font-bold text-primary text-lg">1-2 /an</p>
            <p className="text-xs text-muted-foreground mt-1">Séances d'entretien</p>
          </div>
        </div>
        <p><strong>Zones traitées :</strong> visage (joues, mâchoire, cou, décolleté) et corps (ventre, bras, cuisses). C'est notre soin phare anti-âge à Body by CC Sevran.</p>
      </div>
    ),
  },
  {
    question: "Combien de séances d'épilation laser Diode faut-il sur peau noire ?",
    answer: (
      <div className="space-y-4">
        <p>Sur les peaux foncées (phototypes IV à VI), comptez <strong>8 à 12 séances</strong> espacées de 6 à 8 semaines.</p>
        <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
          <Shield size={16} className="text-accent shrink-0" />
          <p className="text-sm"><strong>Laser Diode</strong> — adapté et sûr pour les peaux noires et métissées, calibré par une infirmière diplômée</p>
        </div>
        <ul className="space-y-2">
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Test de patch réalisé avant la 1ère séance</span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Paramètres calibrés pour chaque phototype</span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Résultats durables : réduction de 80 à 90% des poils</span></li>
        </ul>
      </div>
    ),
  },
  {
    question: 'Comment prendre rendez-vous et quels sont vos horaires ?',
    answer: (
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
          <Clock size={16} className="text-accent shrink-0" />
          <p className="text-sm"><strong>Mardi → Dimanche : 10h - 20h</strong> (fermé le lundi)</p>
        </div>
        <ul className="space-y-2">
          <li className="flex items-start gap-2.5"><Phone size={15} className="text-primary mt-0.5 shrink-0" /><span>Par téléphone : <strong>06 45 23 00 34</strong></span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>En ligne via <strong>Planity</strong> ou sur <strong>bodybycc.fr</strong></span></li>
          <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Sur place : <strong>65 Bd Westinghouse, 93270 Sevran</strong></span></li>
        </ul>
        <p className="text-sm italic text-muted-foreground/80">Un bilan personnalisé est inclus pour tout nouveau soin technique.</p>
      </div>
    ),
  },
  {
    question: 'Proposez-vous des forfaits et formules avantageuses ?',
    answer: (
      <div className="space-y-4">
        <p><strong>Oui</strong>, plusieurs formules combinées à tarif préférentiel :</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-3">
            <p className="font-heading font-semibold text-foreground text-sm">✨ Découverte Visage</p>
            <p className="text-xs text-muted-foreground mt-1">Skincleantech + LED + masque</p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-3">
            <p className="font-heading font-semibold text-foreground text-sm">🌿 Peau Neuve</p>
            <p className="text-xs text-muted-foreground mt-1">Peeling + soin régénérant</p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-3">
            <p className="font-heading font-semibold text-foreground text-sm">💅 Beauté Express</p>
            <p className="text-xs text-muted-foreground mt-1">Manucure + soin rapide</p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/10 p-3">
            <p className="font-heading font-semibold text-foreground text-sm">🏋️ Corps Sculpté</p>
            <p className="text-xs text-muted-foreground mt-1">Pressothérapie + lipolaser</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">+ Forfaits épilation laser multi-séances disponibles.</p>
      </div>
    ),
  },
];

const INITIAL_VISIBLE = 6;

const HomeSoinsSection = () => {
  const [showAll, setShowAll] = useState(false);
  const visibleServices = showAll ? services : services.slice(0, INITIAL_VISIBLE);

  return (
    <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Nos expertises</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Tous nos soins beauté à Sevran</h2>
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl mx-auto font-body">
            Body by CC propose <strong className="text-foreground">10 expertises</strong> en soins esthétiques, toutes réalisées par une infirmière diplômée avec des technologies de pointe.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleServices.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link
                to={`/soins/${s.slug}`}
                onClick={() => window.scrollTo(0, 0)}
                className="block p-6 rounded-xl border border-border bg-background hover:border-primary/30 hover:shadow-lg transition-all group h-full"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <s.icon size={18} className="text-primary" />
                  </div>
                  {s.premium && (
                    <span className="text-[10px] font-heading font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary uppercase tracking-wider">Premium</span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-base text-foreground mb-2">{s.title}</h3>
                <p className="text-xs text-muted-foreground font-body leading-relaxed line-clamp-3">{s.shortDesc}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground/60 font-body">{s.prestations.length} prestation{s.prestations.length > 1 ? 's' : ''}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-primary font-heading font-medium">
                    Découvrir <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {!showAll && services.length > INITIAL_VISIBLE && (
          <button
            onClick={() => setShowAll(true)}
            className="flex items-center gap-2 mx-auto mt-6 text-sm text-primary font-heading font-medium hover:text-primary/80 transition-colors"
          >
            <ChevronDown size={16} />
            Voir {services.length - INITIAL_VISIBLE} autres prestations
          </button>
        )}

        <div className="text-center mt-10">
          <Link
            to="/soins"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            Voir tous les détails <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Index = () => {
  const recentArticles = blogArticles.slice(0, 4);

  useCanonical('/');
  useBreadcrumbSchema([]);
  usePageMeta({
    title: 'Body by CC — Soins esthétiques & beauté médicale à Sevran (93)',
    description: 'Institut beauté Sevran : épilation laser, HIFU, cryothérapie, soins visage. Infirmière diplômée, spécialiste peaux noires. RDV en ligne.',
  });

  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[610px] md:min-h-[680px] flex items-center -mt-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Institut Body by CC — Salle de soins" className="w-full h-full object-cover" fetchPriority="high" width={1920} height={1080} decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-r from-petrol/80 via-petrol/50 to-transparent" />
        </div>
        <div className="relative w-full px-8 md:px-16 pt-28">
          <h1
            className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] max-w-3xl animate-[fadeInUp_0.6s_ease-out_both]"
          >
            Institut de soins beauté<br />& esthétique à Sevran
          </h1>
          <p
            className="mt-6 text-sm md:text-base text-white/80 max-w-2xl leading-relaxed font-body animate-[fadeInUp_0.6s_ease-out_0.15s_both]"
          >
            Body by CC, votre institut beauté à Sevran (93) en Seine-Saint-Denis. Épilation laser, HIFU lifting, cryothérapie minceur, soins visage et corps. Infirmière diplômée, spécialiste des peaux noires et métissées. Retrouvez confiance en vous dans un cadre chaleureux et bienveillant.
          </p>
          <div
            className="mt-8 flex flex-wrap gap-4 animate-[fadeInUp_0.6s_ease-out_0.3s_both]"
          >
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-accent text-accent-foreground rounded-lg px-8 py-4 font-heading font-medium text-base hover:bg-accent/90 transition-colors"
            >
              Prendre rendez-vous
            </a>
            <Link
              to="/soins"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg px-8 py-4 font-heading font-medium text-base hover:bg-white/20 transition-colors"
            >
              Découvrir nos soins <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services highlights */}
      <section className="px-6 lg:px-8 -mt-8 md:-mt-12 relative z-10 pb-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.slice(0, 4).map((s, i) => (
            <div
              key={s.slug}
              className="animate-[fadeInUp_0.4s_ease-out_both]"
              style={{ animationDelay: `${0.4 + i * 0.1}s` }}
            >
              <Link
                to={`/soins/${s.slug}`}
                onClick={() => window.scrollTo(0, 0)}
                className="block p-6 rounded-xl border border-border bg-card shadow-lg hover:shadow-xl hover:border-primary/30 transition-all group"
              >
                <s.icon size={22} className="text-accent mb-3" />
                <h2 className="font-heading text-base font-bold text-foreground">{s.title}</h2>
                <p className="mt-1 text-xs text-muted-foreground font-body line-clamp-2">{s.shortDesc}</p>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Chiffres clés */}
      <section className="px-6 lg:px-8 py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {stats.map((s) => (
            <div key={s.label} className="text-center p-4 md:p-6 rounded-xl border border-border bg-card">
              <p className="font-heading text-2xl md:text-3xl font-bold text-foreground">{s.value}</p>
              <p className="text-xs md:text-sm text-muted-foreground mt-1.5 font-body">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pourquoi Body by CC */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Pourquoi nous choisir</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Un institut de beauté pas comme les autres à Sevran</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-2xl mx-auto font-body">
              Body by CC allie <strong className="text-foreground">expertise médicale</strong> et <strong className="text-foreground">technologies de pointe</strong> dans un cadre bienveillant dédié à votre bien-être.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Stethoscope,
                title: 'Expertise médicale',
                desc: 'Carine Tchuente est infirmière diplômée d\'État. Chaque protocole est conçu avec une rigueur médicale : diagnostic de peau, test de patch, paramètres calibrés selon votre phototype.',
              },
              {
                icon: Heart,
                title: 'Bien-être & confiance en soi',
                desc: 'Ici, l\'esthétique n\'est pas du camouflage. C\'est un accompagnement global pour vous aider à vous sentir mieux dans votre corps et retrouver confiance en vous.',
              },
              {
                icon: Users,
                title: 'Spécialiste peaux noires & métissées',
                desc: 'L\'un des rares instituts du 93 à proposer des soins et une épilation laser Diode adaptés aux phototypes foncés (III à VI), sans risque d\'hyperpigmentation.',
              },
              {
                icon: Zap,
                title: 'Technologies dernier cri',
                desc: 'HIFU lifting, E-SLIM Cryo 360°, Hydrafacial, Skincleantech, photothérapie LED, laser Diode… Des machines professionnelles de dernière génération.',
              },
              {
                icon: HandHeart,
                title: 'Approche personnalisée',
                desc: 'Chaque visite commence par un bilan personnalisé gratuit. Votre soin est adapté à votre type de peau, vos besoins et vos objectifs.',
              },
              {
                icon: Shield,
                title: 'Sécurité & transparence',
                desc: 'Tous nos tarifs sont affichés, les produits sont certifiés, et les protocoles respectent les normes sanitaires les plus strictes. Aucune surprise.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="p-6 md:p-8 rounded-2xl border border-border bg-card hover:border-primary/20 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon size={22} className="text-primary" />
                </div>
                <h3 className="font-heading font-bold text-base md:text-lg text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos expertises */}
      <HomeSoinsSection />

      {/* Formules */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Nos formules</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Formules avantageuses</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto font-body">Profitez de nos combinaisons de soins à tarifs préférentiels.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {formules.map((f) => (
              <div key={f.name} className="p-6 rounded-xl border border-border bg-background hover:border-primary/30 transition-colors flex flex-col h-full">
                <h3 className="font-heading text-base font-bold text-foreground">{f.name}</h3>
                <p className="text-xs text-muted-foreground mt-2 font-body leading-relaxed flex-1">{f.description}</p>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="font-heading text-xl font-bold text-accent">{f.price}</span>
                  <span className="text-xs text-muted-foreground font-body">{f.duration}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              Réserver une formule <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Technologies de pointe */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Nos technologies</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Soins de pointe — Résultats prouvés</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-2xl mx-auto font-body">
              Des <strong className="text-foreground">technologies professionnelles</strong> de dernière génération pour des résultats visibles dès les premières séances.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                title: 'HIFU — Lifting sans chirurgie',
                desc: 'Les ultrasons focalisés de haute intensité stimulent le collagène en profondeur pour un effet lifting naturel. Visage, cou, décolleté et corps. Résultats durables 12 à 18 mois.',
                link: '/soins/hifu',
                badge: 'Anti-âge',
              },
              {
                icon: Snowflake,
                title: 'E-SLIM Cryo 360° — Minceur',
                desc: 'La cryothérapie minceur élimine les cellules graisseuses par le froid ciblé. Jusqu\'à -4 cm dès la première séance. Ventre, cuisses, poignées d\'amour, bras.',
                link: '/soins/cryotherapie',
                badge: 'Minceur',
              },
              {
                icon: Target,
                title: 'Épilation laser Diode',
                desc: 'Épilation définitive adaptée à tous les phototypes, y compris les peaux noires et métissées. Résultats durables dès les premières séances. Visage et corps.',
                link: '/soins/epilation',
                badge: 'Laser',
              },
            ].map((tech, i) => (
              <motion.div
                key={tech.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <Link
                  to={tech.link}
                  onClick={() => window.scrollTo(0, 0)}
                  className="block p-8 rounded-2xl border-2 border-primary/15 bg-primary/[0.03] hover:border-primary/30 hover:shadow-lg transition-all group h-full"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <tech.icon size={22} className="text-primary" />
                    </div>
                    <span className="text-[10px] font-heading font-semibold px-2.5 py-0.5 rounded-full bg-accent/10 text-accent uppercase tracking-wider">{tech.badge}</span>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground mb-3">{tech.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{tech.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary font-heading font-medium group-hover:underline">
                    En savoir plus <ArrowRight size={14} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Notre philosophie */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Notre philosophie</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Prendre soin de son corps,<br />c'est prendre soin de soi</h2>
              <p className="mt-5 text-sm md:text-base text-muted-foreground leading-relaxed font-body">
                Chez Body by CC, nous croyons que l'esthétique va bien au-delà de l'apparence. <strong className="text-foreground">Chaque soin est un acte de bien-être</strong> qui contribue à votre santé physique et mentale. Notre approche, fondée sur l'expertise médicale de Carine Tchuente, place la <strong className="text-foreground">bienveillance et la sécurité</strong> au cœur de chaque protocole.
              </p>
              <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed font-body">
                Que vous veniez pour une séance d'épilation laser Diode, un lifting HIFU ou un simple massage détente, vous êtes accueilli(e) dans un espace chaleureux où votre <strong className="text-foreground">confort et votre confiance</strong> sont la priorité absolue.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/a-propos"
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
                >
                  Découvrir notre histoire <ArrowRight size={14} />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="space-y-4"
            >
              {[
                { icon: Heart, title: 'Bien-être global', desc: 'L\'esthétique comme vecteur de santé. Nous traitons la personne, pas seulement la peau.' },
                { icon: Award, title: 'Excellence & rigueur', desc: 'Formation continue, machines certifiées, produits de qualité professionnelle. Aucun compromis.' },
                { icon: Users, title: 'Inclusivité totale', desc: 'Toutes les carnations, tous les âges, hommes et femmes. Chaque peau mérite une attention experte.' },
                { icon: TrendingUp, title: 'Résultats mesurables', desc: 'Des protocoles dont l\'efficacité est prouvée. Suivi photographique et bilan d\'évolution inclus.' },
              ].map((item, i) => (
                <div key={item.title} className="flex items-start gap-4 p-5 rounded-xl border border-border bg-background hover:border-primary/20 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                    <item.icon size={18} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-sm text-foreground">{item.title}</h3>
                    <p className="text-xs text-muted-foreground font-body mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* À propos teaser */}
      <section className="bg-petrol relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/storefront.png" alt="" className="w-full h-full object-cover opacity-[0.06]" loading="lazy" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24 w-full relative">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-petrol-foreground">Votre institut de beauté à Sevran</h2>
            <p className="mt-4 text-sm md:text-base text-petrol-foreground/70 leading-relaxed font-body">
              Fondé par <strong className="text-petrol-foreground/90">Carine Tchuente, infirmière diplômée</strong> spécialisée en esthétique médicale, Body by CC est bien plus qu'un institut de beauté. C'est un lieu dédié au <strong className="text-petrol-foreground/90">bien-être, à la confiance en soi</strong> et à l'acceptation de son corps.
            </p>
            <p className="mt-3 text-sm md:text-base text-petrol-foreground/70 leading-relaxed font-body">
              Équipé de machines dernier cri — HIFU, E-SLIM Cryo 360°, Épilation laser Diode — chaque soin est une expérience unique, réalisée avec bienveillance et rigueur médicale. Nous accueillons les clientes de <strong className="text-petrol-foreground/90">Sevran, Aulnay-sous-Bois, Villepinte</strong> et de tout le 93.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/a-propos"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline"
              >
                En savoir plus <ArrowRight size={14} />
              </Link>
              <a href="tel:+33645230034" className="inline-flex items-center gap-2 text-petrol-foreground/70 font-heading font-medium text-base hover:text-petrol-foreground transition-colors">
                <Phone size={15} /> 06 45 23 00 34
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <ReviewsSection />

      {/* FAQ */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground text-center mb-10">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`faq-${idx}`} className="border border-border rounded-2xl px-6 md:px-8 bg-card data-[state=open]:border-primary/30 data-[state=open]:shadow-sm">
                <AccordionTrigger className="font-heading text-base md:text-lg font-semibold text-foreground hover:no-underline py-5 text-left">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5 font-body">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link
              to="/faq"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base md:text-lg hover:underline"
            >
              Voir toutes les questions <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog preview */}
      {recentArticles.length > 0 && (
        <section className="px-6 lg:px-8 py-16 md:py-20 bg-secondary/30">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Blog</p>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Nos derniers articles</h2>
                <p className="text-sm text-muted-foreground mt-2 font-body">Conseils beauté, nouveautés et astuces de nos expertes.</p>
              </div>
              <Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary text-primary font-heading font-medium text-sm hover:bg-primary hover:text-primary-foreground transition-colors shrink-0">
                Voir tous les articles <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {recentArticles.map((article, i) => (
                <BlogCard key={article.slug} article={article} index={i} animate />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Villes desservies */}
      <section className="px-6 lg:px-8 py-12 md:py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-accent mb-2">Zones desservies</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Body by CC accueille les clientes de Sevran et alentours</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: 'Aulnay-sous-Bois', slug: 'aulnay-sous-bois' },
              { name: 'Villepinte', slug: 'villepinte' },
              { name: 'Tremblay-en-France', slug: 'tremblay-en-france' },
              { name: 'Livry-Gargan', slug: 'livry-gargan' },
              { name: 'Le Blanc-Mesnil', slug: 'le-blanc-mesnil' },
              { name: 'Drancy', slug: 'drancy' },
              { name: 'Bondy', slug: 'bondy' },
              { name: 'Vaujours', slug: 'vaujours' },
              { name: 'Bobigny', slug: 'bobigny' },
              { name: 'Noisy-le-Sec', slug: 'noisy-le-sec' },
            ].map((city) => (
              <Link
                key={city.slug}
                to={`/ville/${city.slug}`}
                onClick={() => window.scrollTo(0, 0)}
                className="px-5 py-2.5 rounded-full border border-border bg-card text-sm font-heading font-medium text-foreground hover:border-primary/40 hover:text-primary transition-colors"
              >
                {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Nous trouver */}
      <ContactSection />

      {/* CTA */}
      <CTASection
        title="Prêt(e) à prendre soin de vous ?"
        subtitle="Prenez rendez-vous en ligne et offrez-vous un moment de bien-être chez Body by CC."
        buttonLabel="Prendre rendez-vous"
        buttonLink={PLANITY_URL}
        isExternal
      />

      <Suspense fallback={null}>
        <StickyCTABanner label="Prendre rendez-vous" href={PLANITY_URL} subtitle="Découvrez nos soins visage, corps et bien-être à Sevran." />
      </Suspense>
    </>
  );
};

export default Index;